import { useCallback, useState } from 'react';

import { expectedUserVar } from './useSessionSlots';
import { useWorkspace } from './useWorkspace';

const GRAPHQL_ENDPOINT =
	import.meta.env.VITE_GRAPHQL_ENDPOINT ?? 'https://api.ontreams.com/graphql';
const STREAM_BASE_URL = GRAPHQL_ENDPOINT.replace('/graphql', '/stream/agent');

export function useStreamAgent() {
	const workspace = useWorkspace();

	const [text, setText] = useState('');

	const stream = useCallback(
		async (prompt: string, params: Record<string, string> = {}, signal?: AbortSignal) => {
			setText('');

			const expectedUserID = expectedUserVar();

			const url = `${STREAM_BASE_URL}?${new URLSearchParams({ prompt, ...params })}`;
			const response = await fetch(url, {
				method: 'POST',
				credentials: 'include',
				headers: {
					'X-Treams-Workspace': workspace.id,
					...(expectedUserID ? { 'X-Treams-User': expectedUserID } : {}),
				},
				signal,
			});

			if (!response.ok || !response.body) {
				throw new Error(`Stream request failed with status ${response.status}`);
			}

			const reader = response.body.getReader();
			const decoder = new TextDecoder();
			let accumulated = '';

			try {
				while (true) {
					const { done, value } = await reader.read();
					if (done) break;
					const chunk = decoder.decode(value, { stream: true });
					accumulated += chunk;
					setText((prev) => prev + chunk);
				}
			} catch (err) {
				if (err instanceof DOMException && err.name === 'AbortError') {
					return accumulated;
				}
				throw err;
			}

			return accumulated;
		},
		[workspace.id],
	);

	return { stream, text };
}
